import { tenantOverrideForLocation } from '../config/productBoundary';
// =============================================================================
// SUPABASE CLIENT CONFIGURATION
// Single source of truth for Supabase connection
// =============================================================================

import { createClient, SupabaseClient, AuthChangeEvent, Session, User, AuthError } from '@supabase/supabase-js';
import { Database } from '../types/supabase';
import { logger } from '../utils/logger';
import { tenantSlugForHost } from '../config/hostTenants';

const log = logger.module('Supabase');

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!supabaseUrl || !supabaseAnonKey) {
  log.error('Missing Supabase environment variables. Check your .env file.');
}

export const supabase: SupabaseClient<Database> = createClient<Database>(
  supabaseUrl || '', 
  supabaseAnonKey || '', 
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true
    }
  }
);

// =============================================================================
// AUTH HELPERS
// =============================================================================

interface AuthResponse<T = any> {
  data: T | null;
  error: AuthError | null;
}

/**
 * Sign up a new user with email and password
 */
export async function signUp(
  email: string, 
  password: string, 
  metadata: Record<string, any> = {}
): Promise<AuthResponse> {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: metadata
    }
  });
  return { data, error };
}

/**
 * Sign in with email and password
 */
export async function signIn(email: string, password: string): Promise<AuthResponse> {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  });
  return { data, error };
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<{ error: AuthError | null }> {
  const { error } = await supabase.auth.signOut();
  return { error };
}

/**
 * Get the current user
 */
export async function getCurrentUser(): Promise<{ user: User | null; error: AuthError | null }> {
  const { data: { user }, error } = await supabase.auth.getUser();
  return { user, error };
}

/**
 * Get the current session
 */
export async function getSession(): Promise<{ session: Session | null; error: AuthError | null }> {
  const { data: { session }, error } = await supabase.auth.getSession();
  return { session, error };
}

/**
 * Listen for auth state changes
 */
export function onAuthStateChange(
  callback: (event: AuthChangeEvent, session: Session | null) => void
) {
  return supabase.auth.onAuthStateChange(callback);
}

// =============================================================================
// TENANT HELPERS
// =============================================================================

let _cachedTenantId: string | null = null;

/** Call this whenever the active tenant changes (switchTenant, exitImpersonation). */
export function clearTenantIdCache(): void {
  _cachedTenantId = null;
}

/**
 * Get the current user's tenant_id.
 * Product hosts: explicit authorized admin workspace → host business.
 * Unmapped hosts retain the legacy profile/membership behavior.
 * Result is cached until clearTenantIdCache() is called, which
 * TenantContext does at the start of every switchTenant/exitImpersonation.
 */
export async function getCurrentTenantId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) { clearTenantIdCache(); return null; }
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const hostSlug = tenantSlugForHost(hostname);
  // Product-domain resolution must not reuse another account or workspace's cache.
  if (!hostSlug && _cachedTenantId) return _cachedTenantId;
  const { data: profile, error: profileError } = await supabase.from('profiles')
    .select('active_tenant_id, is_platform_admin').eq('id', user.id).maybeSingle();
  if (profileError) throw profileError;
  const legacyId = (profile as any)?.active_tenant_id ?? null;
  const explicitId = typeof window !== 'undefined' ? tenantOverrideForLocation(window.location, legacyId) : legacyId;
  if (hostSlug) {
    let query = supabase.from('tenants').select('id').eq('is_active', true);
    query = explicitId ? query.eq('id', explicitId) : query.eq('slug', hostSlug);
    const { data: tenant, error } = await query.maybeSingle();
    if (error) throw error;
    if (!tenant) return null;
    const id = (tenant as any).id as string;
    if (!(profile as any)?.is_platform_admin) {
      const { data: member, error: memberError } = await supabase.from('tenant_members')
        .select('tenant_id').eq('user_id', user.id).eq('tenant_id', id).maybeSingle();
      if (memberError) throw memberError;
      if (!member) return null;
    }
    return id;
  }
  if (legacyId) { _cachedTenantId = legacyId; return legacyId; }
  const { data: membership } = await supabase.from('tenant_members').select('tenant_id')
    .eq('user_id', user.id).limit(1).maybeSingle();
  _cachedTenantId = (membership as any)?.tenant_id ?? null;
  return _cachedTenantId;
}

/**
 * Get tenant_id or throw — use in insert operations where tenant_id is required.
 */
export async function requireTenantId(): Promise<string> {
  const tenantId = await getCurrentTenantId();
  if (!tenantId) {
    throw new Error('No tenant_id found — user must complete onboarding first');
  }
  return tenantId;
}

/**
 * Get tenant_id for public/unauthenticated pages (quote forms, SMS opt-in, etc.)
 * Known product domains resolve only their registered slug; no unrelated fallback.
 * Unmapped deployments retain the legacy first-active-tenant fallback.
 */
export async function getPublicTenantId(): Promise<string | null> {
  // Public forms belong to the product domain, never an admin's selected tenant.

  // Try domain-based resolution (*.blissbunkbeds.com → bliss-bedrooms)
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname) {
    const hostSlug = tenantSlugForHost(hostname);
    if (hostSlug) {
      const { data } = await supabase
        .from('tenants')
        .select('id')
        .eq('slug', hostSlug)
        .eq('is_active', true)
        .maybeSingle();
      return (data as any)?.id ?? null;
    }
  }

  // Fallback: first active tenant (works for single-tenant deployments)
  const { data } = await supabase
    .from('tenants')
    .select('id')
    .eq('is_active', true)
    .limit(1)
    .maybeSingle();
  return (data as any)?.id || null;
}

/**
 * Clear cached tenant_id (call on sign-out)
 */
export function clearTenantCache(): void {
  _cachedTenantId = null;
}

// Listen for sign-out to clear cache
supabase.auth.onAuthStateChange((event) => {
  if (event === 'SIGNED_OUT') {
    _cachedTenantId = null;
  }
});

// =============================================================================
// DATABASE HELPERS
// =============================================================================

interface FetchOptions {
  select?: string;
  eq?: Record<string, any>;
  order?: {
    column: string;
    ascending?: boolean;
  };
  limit?: number;
}

interface DatabaseResponse<T = any> {
  data: T | null;
  error: any;
}

/**
 * Generic fetch function with error handling
 */
export async function fetchFromTable<T = any>(
  table: string, 
  options: FetchOptions = {}
): Promise<DatabaseResponse<T[]>> {
  let query = supabase.from(table).select(options.select || '*');
  
  if (options.eq) {
    Object.entries(options.eq).forEach(([column, value]) => {
      query = query.eq(column, value);
    });
  }
  
  if (options.order) {
    query = query.order(options.order.column, { ascending: options.order.ascending ?? false });
  }
  
  if (options.limit) {
    query = query.limit(options.limit);
  }
  
  const { data, error } = await query;
  
  if (error) {
    log.error(`Error fetching from ${table}:`, error);
    return { data: null, error };
  }
  
  return { data: data as T[], error: null };
}

/**
 * Generic insert function
 */
export async function insertIntoTable<T = any>(
  table: string, 
  data: any
): Promise<DatabaseResponse<T[]>> {
  const { data: result, error } = await supabase
    .from(table)
    .insert(data)
    .select();
  
  if (error) {
    log.error(`Error inserting into ${table}:`, error);
    return { data: null, error };
  }
  
  return { data: result as T[], error: null };
}

/**
 * Generic update function
 */
export async function updateInTable<T = any>(
  table: string, 
  id: string | number, 
  updates: any, 
  idColumn: string = 'id'
): Promise<DatabaseResponse<T[]>> {
  const { data: result, error } = await supabase
    .from(table)
    .update(updates)
    .eq(idColumn, id)
    .select();
  
  if (error) {
    log.error(`Error updating ${table}:`, error);
    return { data: null, error };
  }
  
  return { data: result as T[], error: null };
}

/**
 * Generic delete function
 */
export async function deleteFromTable(
  table: string, 
  id: string | number, 
  idColumn: string = 'id'
): Promise<{ success: boolean; error: any }> {
  const { error } = await supabase
    .from(table)
    .delete()
    .eq(idColumn, id);
  
  if (error) {
    log.error(`Error deleting from ${table}:`, error);
    return { success: false, error };
  }
  
  return { success: true, error: null };
}

// =============================================================================
// REALTIME SUBSCRIPTIONS
// =============================================================================

/**
 * Subscribe to changes on a table
 */
export function subscribeToTable(
  table: string, 
  callback: (payload: any) => void, 
  filter: string | null = null
) {
  let channel = supabase
    .channel(`${table}_changes`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: table,
        ...(filter && { filter })
      },
      callback
    )
    .subscribe();
  
  return channel;
}

/**
 * Unsubscribe from a channel
 */
export function unsubscribe(channel: any): void {
  supabase.removeChannel(channel);
}

export default supabase;

